import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar.jsx';
import { api } from '../api/client.js';

const initial = {
  name: '',
  description: '',
  url: 'https://',
  category: 'Custom',
  authType: 'SSO',
  iconColor: 'purple',
  iconLabel: '',
  anthropicApiKeyId: '',
  pricing: { inputPerMillion: 3, outputPerMillion: 15, perCall: '' }
};

export default function AddApp() {
  const [form, setForm] = useState(initial);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updatePricing = (k, v) => setForm((f) => ({ ...f, pricing: { ...f.pricing, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const initials = form.iconLabel || form.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
      const payload = {
        ...form,
        iconLabel: initials,
        pricing: {
          inputPerMillion: parseFloat(form.pricing.inputPerMillion) || 0,
          outputPerMillion: parseFloat(form.pricing.outputPerMillion) || 0,
          perCall: form.pricing.perCall ? parseFloat(form.pricing.perCall) : null
        }
      };
      await api.post('/api/apps', payload);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="max-w-xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-medium mb-1">Add a new app</h1>
        <p className="text-sm text-muted mb-6">
          Register an internal AI app. Apps marked SSO receive a signed launch token.
        </p>

        <form onSubmit={submit} autoComplete="off" className="bg-white border border-border rounded-xl p-6 space-y-4">
          <Field label="Name">
            <input required maxLength={60} className="input" value={form.name}
              onChange={(e) => update('name', e.target.value)} />
          </Field>
          <Field label="Description">
            <input maxLength={200} className="input" value={form.description}
              onChange={(e) => update('description', e.target.value)} />
          </Field>
          <Field label="App URL (HTTPS)">
            <input required type="url" className="input" value={form.url}
              onChange={(e) => update('url', e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category">
              <select className="input" value={form.category} onChange={(e) => update('category', e.target.value)}>
                <option>Generative</option><option>Productivity</option>
                <option>Analysis</option><option>Chat</option><option>Custom</option>
              </select>
            </Field>
            <Field label="Auth type">
              <select className="input" value={form.authType} onChange={(e) => update('authType', e.target.value)}>
                <option value="SSO">SSO (Google passthrough)</option>
                <option value="SEPARATE">Separate login</option>
                <option value="APIKEY">API key</option>
                <option value="NONE">No auth</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Icon color">
              <select className="input" value={form.iconColor} onChange={(e) => update('iconColor', e.target.value)}>
                <option value="purple">Purple</option><option value="teal">Teal</option>
                <option value="coral">Coral</option><option value="blue">Blue</option>
                <option value="amber">Amber</option><option value="pink">Pink</option>
              </select>
            </Field>
            <Field label="Icon label (1-2 chars)">
              <input maxLength={2} className="input" value={form.iconLabel}
                onChange={(e) => update('iconLabel', e.target.value.toUpperCase())} placeholder="auto" />
            </Field>
          </div>

          <div className="border-t border-border pt-4">
            <p className="text-sm font-medium mb-1">Pricing</p>
            <p className="text-xs text-muted mb-3">Used to compute cost when usage is reported.</p>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Input $/1M">
                <input type="number" step="0.01" min="0" className="input"
                  value={form.pricing.inputPerMillion}
                  onChange={(e) => updatePricing('inputPerMillion', e.target.value)} />
              </Field>
              <Field label="Output $/1M">
                <input type="number" step="0.01" min="0" className="input"
                  value={form.pricing.outputPerMillion}
                  onChange={(e) => updatePricing('outputPerMillion', e.target.value)} />
              </Field>
              <Field label="Per-call $ (optional)">
                <input type="number" step="0.001" min="0" className="input"
                  value={form.pricing.perCall} placeholder="0.04"
                  onChange={(e) => updatePricing('perCall', e.target.value)} />
              </Field>
            </div>
            <p className="text-[11px] text-muted mt-2">
              Per-call for image gen. Per-million for chat/text models.
            </p>
          </div>

          <div className="border-t border-border pt-4">
            <Field label="Anthropic API key ID (optional)">
              <input className="input font-mono" placeholder="apikey_01ABC..."
                value={form.anthropicApiKeyId}
                onChange={(e) => update('anthropicApiKeyId', e.target.value)}
                autoComplete="off" />
              <p className="text-[11px] text-muted mt-1">
                Maps this app to an Anthropic API key for automatic usage sync from the Anthropic Admin API.
                Once saved, the value will be hidden and require re-entry to change.
              </p>
            </Field>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => navigate('/')} className="text-sm px-4 py-2 rounded-lg hover:bg-neutral-50">
              Cancel
            </button>
            <button disabled={submitting} className="text-sm bg-ink text-white px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50">
              {submitting ? 'Adding…' : 'Add to launcher'}
            </button>
          </div>
        </form>
      </main>
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs text-muted mb-1.5">{label}</span>
      {children}
    </label>
  );
}
